import { useRef, useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Font imports
import '@fontsource/quicksand/400.css';
import '@fontsource/quicksand/500.css';
import '@fontsource/quicksand/600.css';
import '@fontsource/quicksand/700.css';
import '@fontsource/mulish/400.css';
import '@fontsource/mulish/500.css';
import '@fontsource/mulish/600.css';
import '@fontsource/andika/400.css';

// Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import StoryCreator from "./pages/StoryCreator";
import StoryViewer from "./pages/StoryViewer";
import MyStories from "./pages/MyStories";
import StudentFolders from "./pages/StudentFolders";
import ProgressTracking from "./pages/ProgressTracking";
import UDLStrategies from "./pages/UDLStrategies";
import PresentLevels from "./pages/PresentLevels";
import IEPGoals from "./pages/IEPGoals";
import IEPGoalTracker from "./pages/IEPGoalTracker";
import WorksheetGenerator from "./pages/WorksheetGenerator";
import Teams from "./pages/Teams";
import IEPReview from "./pages/IEPReview";
import Admin from "./pages/Admin";
import SupportToolkit from "./pages/SupportToolkit";

// Components
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          // Clear hash and navigate to dashboard with user data
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { state: { user: response.data }, replace: true });
        } catch (error) {
          console.error('Auth error:', error);
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };
    
    processSession();
  }, [navigate]);
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-text-secondary font-body">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // If user passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }
    
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/');
      }
    };
    
    checkAuth();
  }, [navigate, location.state]);
  
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return null;
  }
  
  // Clone children with user prop
  return children({ user });
};

// App Router Component
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - MUST be synchronous
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/create" 
        element={
          <ProtectedRoute>
            {({ user }) => <StoryCreator user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/story/:storyId" 
        element={
          <ProtectedRoute>
            {({ user }) => <StoryViewer user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/my-stories" 
        element={
          <ProtectedRoute>
            {({ user }) => <MyStories user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/students" 
        element={
          <ProtectedRoute>
            {({ user }) => <StudentFolders user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/students/:folderId" 
        element={
          <ProtectedRoute>
            {({ user }) => <StudentFolders user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/progress" 
        element={
          <ProtectedRoute>
            {({ user }) => <ProgressTracking user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/progress/:studentId" 
        element={
          <ProtectedRoute>
            {({ user }) => <ProgressTracking user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/udl-strategies" 
        element={
          <ProtectedRoute>
            {({ user }) => <UDLStrategies user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/present-levels" 
        element={
          <ProtectedRoute>
            {({ user }) => <PresentLevels user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/iep-goals" 
        element={
          <ProtectedRoute>
            {({ user }) => <IEPGoals user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/iep-goal-tracker" 
        element={
          <ProtectedRoute>
            {({ user }) => <IEPGoalTracker user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/iep-goal-tracker/:trackerId" 
        element={
          <ProtectedRoute>
            {({ user }) => <IEPGoalTracker user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/worksheets" 
        element={
          <ProtectedRoute>
            {({ user }) => <WorksheetGenerator user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/teams" 
        element={
          <ProtectedRoute>
            {({ user }) => <Teams user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/iep-review" 
        element={
          <ProtectedRoute>
            {({ user }) => <IEPReview user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            {({ user }) => <Admin user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/support-toolkit" 
        element={
          <ProtectedRoute>
            {({ user }) => <SupportToolkit user={user} />}
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
